import {
  AnalyticsClientApiConnectionArgs,
  ApiConnectionArgs
} from '@dtx-company/inter-app/src/types/analytics'
import {
  EventType,
  RangeSummaryData
} from '@dtx-company/connect-codegen/src/gen/analytics/v2/analytics_pb'
import {
  OrgActivityRequest,
  OrgActivityResponse,
  SummaryRequest,
  SummaryResponse,
  TopCitiesRequest,
  TopCitiesResponse,
  TopDaysRequest,
  TopDaysResponse,
  TopHoursRequest,
  TopHoursResponse,
  TopOperatingSystemsRequest,
  TopOperatingSystemsResponse,
  TopStatesRequest,
  TopStatesResponse
} from '@dtx-company/connect-codegen/src/gen/analytics/v2/service_pb'
import { Timestamp } from '@bufbuild/protobuf'
import { getHeaders, prepParams, baseApiUri as uri } from '../utils'
import { logger } from '@dtx-company/logger'
import analyticsClient from '@dtx-company/inter-app/src/connect-client/analyticsClient'

/**
 *
 * Fetches range summary data for Flowcode or Flowpage
 */
export async function getRangeSummary(
  args: AnalyticsClientApiConnectionArgs
): Promise<Partial<RangeSummaryData> | undefined> {
  try {
    const eid: EventType[] =
      typeof args.params.event_type_id === 'number'
        ? [args.params.event_type_id]
        : (args.params.event_type_id as EventType[])

    const resp = await analyticsClient.rangeSummary(
      {
        eventType: eid,
        allAssets: true,
        product: args.params.product_id,
        startDate: Timestamp.fromDate(new Date(args.params.start_date || '')),
        endDate: Timestamp.fromDate(new Date(args.params.end_date || ''))
      },
      { headers: { Authorization: 'Bearer ' + args.token?.token } }
    )
    return resp.data
  } catch (e) {
    console.error('error fetching range summary: ', e.message)
    logger.logError(e, { team: 'flowlytics', tag: 'welcome-toast' })
    return undefined
  }
}

/**
 *
 * Fetches top connections data by hour
 */
export const getHourOfDay = async (
  params: TopHoursRequest,
  token: string
): Promise<TopHoursResponse | null> => {
  try {
    return await analyticsClient.topHours(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching hour of day', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by day of the week
 */
export const getDayOfWeek = async (
  params: TopDaysRequest,
  token: string
): Promise<TopDaysResponse | null> => {
  try {
    return await analyticsClient.topDays(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching day of week', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by operating system
 * Also currently used for "device type" charts
 */
export const getTopOperatingSystems = async (
  params: TopOperatingSystemsRequest,
  token: string
): Promise<TopOperatingSystemsResponse | null> => {
  try {
    return await analyticsClient.topOperatingSystems(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching top operating systems', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by city
 */
export const getTopByCity = async (
  params: TopCitiesRequest,
  token: string
): Promise<TopCitiesResponse | null> => {
  try {
    return await analyticsClient.topCities(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching top cities', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by state
 */
export const getTopByState = async (
  params: TopStatesRequest,
  token: string
): Promise<TopStatesResponse | null> => {
  try {
    return await analyticsClient.topStates(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching top states', e.message)
    return null
  }
}

/**
 *
 * Fetches a zip file for a user's data report as CSV files
 */
export interface ExportReportOptions {
  exportType?: string
}
export async function exportFlowlyticsReport(
  args: ApiConnectionArgs,
  options?: ExportReportOptions
): Promise<string | null> {
  const exportType = options?.exportType || 'v2'

  try {
    const resp = await fetch(`${uri}/download/${exportType}?${prepParams(args.params)}`, {
      method: 'GET',
      headers: getHeaders(args.token)
    })
    const blob = await resp.blob()
    return URL.createObjectURL(blob)
  } catch (e) {
    console.error('Error generating CSV download: ', e.message)
    return null
  }
}

export const getAnalyticsSummary = async (
  params: SummaryRequest,
  token: string
): Promise<SummaryResponse | null> => {
  try {
    return await analyticsClient.summary(params, { headers: { Authorization: `Bearer ${token}` } })
  } catch (e) {
    console.error('error fetching analytics summary: ', e.message)
    return null
  }
}

export const getOrgActivity = async (
  params: OrgActivityRequest,
  token: string
): Promise<OrgActivityResponse | null> => {
  try {
    return await analyticsClient.orgActivity(params, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (e) {
    console.error('error fetching org activity: ', e.message)
    return null
  }
}
